/**
 * Project document schema.
 *
 * The zod schemas are the single source of truth: TypeScript types are inferred from them and the
 * server uses them to validate documents on every save. Any change here that is not backwards
 * compatible must bump {@link SCHEMA_VERSION} and add a migration in `migrations.ts`.
 *
 * Time conventions:
 *  - Timeline positions/durations are integer **frames** at the project frame rate.
 *  - Keyframe positions are integer frames **relative to the clip start**.
 *  - Positions inside source media (`sourceIn`) are **seconds**, since media may have any frame rate.
 */
import { z } from 'zod';

export const SCHEMA_VERSION = 4;

export const IdSchema = z.string().min(1);
export type Id = z.infer<typeof IdSchema>;

const Frame = z.number().int();
const NonNegFrame = z.number().int().nonnegative();
const PositiveFrame = z.number().int().positive();

export const RationalSchema = z.object({
  num: z.number().int().positive(),
  den: z.number().int().positive(),
});
export type Rational = z.infer<typeof RationalSchema>;

export const ColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/, 'Expected #rrggbb or #rrggbbaa');

// ---------------------------------------------------------------------------------------------
// Animation
// ---------------------------------------------------------------------------------------------

export const EasingSchema = z.enum(['linear', 'hold', 'easeIn', 'easeOut', 'easeInOut']);
export type Easing = z.infer<typeof EasingSchema>;

export const KeyframeSchema = z.object({
  id: IdSchema,
  /** Frame relative to the owning clip's start. */
  frame: Frame,
  value: z.number(),
  /** Interpolation used from this keyframe towards the next one. */
  easing: EasingSchema,
});
export type Keyframe = z.infer<typeof KeyframeSchema>;

export const AnimatableSchema = z.object({
  /** Static value, used when there are no keyframes. */
  value: z.number(),
  /** Sorted by frame, unique frames. Empty ⇒ not animated. */
  keyframes: z.array(KeyframeSchema),
});
export type Animatable = z.infer<typeof AnimatableSchema>;

export const ParamKeySchema = z.enum([
  'x',
  'y',
  'scale',
  'scaleX',
  'scaleY',
  'rotation',
  'opacity',
  'volume',
  'cropLeft',
  'cropRight',
  'cropTop',
  'cropBottom',
]);
export type ParamKey = z.infer<typeof ParamKeySchema>;

export const ParamsSchema = z.partialRecord(ParamKeySchema, AnimatableSchema);
export type Params = z.infer<typeof ParamsSchema>;

// ---------------------------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------------------------

export const MediaKindSchema = z.enum(['video', 'audio', 'image']);
export type MediaKind = z.infer<typeof MediaKindSchema>;

/** How a media item entered the project. A union so new import paths can be added later. */
export const MediaOriginSchema = z.discriminatedUnion('type', [
  /** Imported from a file (picked, dropped, or already in the project folder). */
  z.object({ type: z.literal('file'), fileName: z.string() }),
]);
export type MediaOrigin = z.infer<typeof MediaOriginSchema>;

/**
 * Where a media file's bytes live. Interpreted by the storage provider named in `provider`
 * (e.g. `local-folder`: `ref` is a path relative to the project folder). The provider id
 * is an open string so new storage strategies need no schema change.
 */
export const MediaLocationSchema = z.object({
  provider: z.string().min(1),
  ref: z.string().min(1),
  /** Size in bytes when imported; used to detect a changed or wrongly relinked file. */
  size: z.number().int().nonnegative().optional(),
  /** Source file modification time (ms since epoch) when imported. */
  lastModified: z.number().optional(),
});
export type MediaLocation = z.infer<typeof MediaLocationSchema>;

/** Immutable, probed facts about an imported media file, and where its bytes live. */
export const MediaAssetSchema = z.object({
  id: IdSchema,
  name: z.string(),
  kind: MediaKindSchema,
  /** Seconds. Absent for still images (infinite). */
  duration: z.number().positive().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  fps: z.number().positive().optional(),
  hasAudio: z.boolean(),
  hasVideo: z.boolean(),
  origin: MediaOriginSchema,
  location: MediaLocationSchema,
  importedAt: z.string(),
  /**
   * When the media was recorded (ISO 8601), from the file's own metadata: EXIF `DateTimeOriginal`
   * for photos, container creation time for video/audio. Absent if the file doesn't say.
   */
  capturedAt: z.string().optional(),
  /** The library folder the media is filed in; absent ⇒ top level. */
  folderId: IdSchema.optional(),
});
export type MediaAsset = z.infer<typeof MediaAssetSchema>;

/** A folder in the media library, used only for organising the bin (no effect on files on disk). */
export const MediaFolderSchema = z.object({
  id: IdSchema,
  name: z.string(),
  /** Absent ⇒ top level. */
  parentId: IdSchema.optional(),
});
export type MediaFolder = z.infer<typeof MediaFolderSchema>;

// ---------------------------------------------------------------------------------------------
// Clips
// ---------------------------------------------------------------------------------------------

export const FitModeSchema = z.enum(['contain', 'cover', 'stretch', 'none']);
export type FitMode = z.infer<typeof FitModeSchema>;

export const BlendModeSchema = z.enum(['normal', 'add', 'multiply', 'screen']);
export type BlendMode = z.infer<typeof BlendModeSchema>;

export const TextStyleSchema = z.object({
  fontFamily: z.string(),
  fontSize: z.number().positive(),
  fontWeight: z.number().int().min(100).max(900),
  italic: z.boolean(),
  color: ColorSchema,
  align: z.enum(['left', 'center', 'right']),
  lineHeight: z.number().positive(),
  strokeColor: ColorSchema,
  strokeWidth: z.number().nonnegative(),
  shadowColor: ColorSchema,
  shadowBlur: z.number().nonnegative(),
  backgroundColor: ColorSchema.optional(),
  padding: z.number().nonnegative(),
});
export type TextStyle = z.infer<typeof TextStyleSchema>;

const ClipBaseShape = {
  id: IdSchema,
  name: z.string(),
  /** Timeline start, frames. */
  start: NonNegFrame,
  /** Timeline duration, frames. */
  duration: PositiveFrame,
  enabled: z.boolean(),
  params: ParamsSchema,
};

const VisualShape = {
  fit: FitModeSchema,
  blendMode: BlendModeSchema,
};

export const VideoClipSchema = z.object({
  ...ClipBaseShape,
  ...VisualShape,
  type: z.literal('video'),
  mediaId: IdSchema,
  /** Offset into the media, seconds. */
  sourceIn: z.number().nonnegative(),
  speed: z.number().positive(),
  muted: z.boolean(),
});

export const ImageClipSchema = z.object({
  ...ClipBaseShape,
  ...VisualShape,
  type: z.literal('image'),
  mediaId: IdSchema,
});

export const AudioClipSchema = z.object({
  ...ClipBaseShape,
  type: z.literal('audio'),
  mediaId: IdSchema,
  sourceIn: z.number().nonnegative(),
  speed: z.number().positive(),
  muted: z.boolean(),
});

export const TextClipSchema = z.object({
  ...ClipBaseShape,
  ...VisualShape,
  type: z.literal('text'),
  text: z.string(),
  style: TextStyleSchema,
});

export const SolidClipSchema = z.object({
  ...ClipBaseShape,
  ...VisualShape,
  type: z.literal('solid'),
  color: ColorSchema,
});

/** A nested sequence ("group"/"chapter") placed on a track of another sequence. */
export const SequenceClipSchema = z.object({
  ...ClipBaseShape,
  ...VisualShape,
  type: z.literal('sequence'),
  sequenceId: IdSchema,
  /** Offset into the nested sequence, frames. */
  sourceIn: NonNegFrame,
  muted: z.boolean(),
});

export const ClipSchema = z.discriminatedUnion('type', [
  VideoClipSchema,
  ImageClipSchema,
  AudioClipSchema,
  TextClipSchema,
  SolidClipSchema,
  SequenceClipSchema,
]);
export type VideoClip = z.infer<typeof VideoClipSchema>;
export type ImageClip = z.infer<typeof ImageClipSchema>;
export type AudioClip = z.infer<typeof AudioClipSchema>;
export type TextClip = z.infer<typeof TextClipSchema>;
export type SolidClip = z.infer<typeof SolidClipSchema>;
export type SequenceClip = z.infer<typeof SequenceClipSchema>;
export type Clip = z.infer<typeof ClipSchema>;
export type ClipType = Clip['type'];
export type VisualClip = VideoClip | ImageClip | TextClip | SolidClip | SequenceClip;

// ---------------------------------------------------------------------------------------------
// Tracks, transitions, sequences
// ---------------------------------------------------------------------------------------------

export const TransitionTypeSchema = z.enum([
  'crossfade',
  'dipToBlack',
  'dipToWhite',
  'wipeLeft',
  'wipeRight',
  'wipeUp',
  'wipeDown',
  'slideLeft',
  'slideRight',
  'zoom',
  'circle',
]);
export type TransitionType = z.infer<typeof TransitionTypeSchema>;

/**
 * A transition sits centred on the cut between two adjacent clips (`from.end === to.start`).
 * It spans `[cut - floor(duration/2), cut - floor(duration/2) + duration)`; media handles beyond
 * the clips' edges are used (and frozen at the media boundaries if unavailable).
 */
export const TransitionSchema = z.object({
  id: IdSchema,
  type: TransitionTypeSchema,
  duration: PositiveFrame,
  fromClipId: IdSchema,
  toClipId: IdSchema,
});
export type Transition = z.infer<typeof TransitionSchema>;

export const TrackKindSchema = z.enum(['video', 'audio']);
export type TrackKind = z.infer<typeof TrackKindSchema>;

export const TrackSchema = z.object({
  id: IdSchema,
  kind: TrackKindSchema,
  name: z.string(),
  /** Sorted by `start`, never overlapping. */
  clips: z.array(ClipSchema),
  transitions: z.array(TransitionSchema),
  muted: z.boolean(),
  solo: z.boolean(),
  locked: z.boolean(),
  /** Video tracks only: excluded from the picture when true. */
  hidden: z.boolean(),
  /** Linear gain applied to all audio on the track. */
  volume: z.number().min(0).max(4),
});
export type Track = z.infer<typeof TrackSchema>;

export const MarkerSchema = z.object({
  id: IdSchema,
  frame: NonNegFrame,
  label: z.string(),
  color: ColorSchema,
});
export type Marker = z.infer<typeof MarkerSchema>;

export const SequenceSchema = z.object({
  id: IdSchema,
  name: z.string(),
  /** Index 0 is the bottom-most layer (V1). */
  videoTracks: z.array(TrackSchema),
  /** Index 0 is A1. */
  audioTracks: z.array(TrackSchema),
  markers: z.array(MarkerSchema),
});
export type Sequence = z.infer<typeof SequenceSchema>;

export const ProjectSettingsSchema = z.object({
  width: z.number().int().min(16).max(8192),
  height: z.number().int().min(16).max(8192),
  fps: RationalSchema,
  sampleRate: z.number().int().positive(),
  backgroundColor: ColorSchema,
  /**
   * Colour space layers are blended in: `linear` light (physically correct fades and blend modes)
   * or gamma-encoded `srgb` (how projects created before schema version 4 were rendered).
   */
  blending: z.enum(['linear', 'srgb']),
});
export type ProjectSettings = z.infer<typeof ProjectSettingsSchema>;

export const ProjectSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  id: IdSchema,
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  settings: ProjectSettingsSchema,
  rootSequenceId: IdSchema,
  sequences: z.record(IdSchema, SequenceSchema),
  /** The project's media bin ("library"). */
  media: z.record(IdSchema, MediaAssetSchema),
  /** Display order of media in the bin. */
  mediaOrder: z.array(IdSchema),
  /** Library folders (nestable via `parentId`). */
  mediaFolders: z.record(IdSchema, MediaFolderSchema),
});
export type Project = z.infer<typeof ProjectSchema>;
